import React, { memo } from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';

/**
 * The one way this app labels an icon-only control.
 *
 * Before this, every toolbar used the native `title` attribute: a ~1s unstyled
 * browser delay, no keyboard-focus support, and shortcuts smuggled into the
 * string as `"Refresh (⌘R)"`. `Tip` replaces all of it — and, more usefully,
 * guarantees an `aria-label`, since a bare icon button is otherwise announced
 * as just "button".
 *
 * Radix tooltips deliberately never open on tap, per WAI-ARIA guidance. That is
 * accepted: on touch the `aria-label` is what carries the meaning, and it is
 * always there.
 */

// ⌘ on Apple, Ctrl everywhere else. Resolved once at module scope — it cannot
// change mid-session, and every shortcut string in the app currently hardcodes
// ⌘, which is simply wrong on Windows and Linux.
const PLATFORM =
    (typeof navigator !== 'undefined' &&
        (navigator.userAgentData?.platform || navigator.platform)) || '';

export const MOD = /mac|iphone|ipad|ipod/i.test(PLATFORM) ? '⌘' : 'Ctrl';

const KBD_CLASSES =
    'rounded border border-white/10 bg-white/5 px-1 py-px font-mono text-[10px] leading-normal text-muted-foreground';

/**
 * @param {string}  label    Spoken name of the control. Also the tooltip text.
 * @param {string} [hint]    Keyboard shortcut, rendered as a chip. Use `MOD`
 *                           rather than a literal ⌘.
 * @param {string} [side]    Radix placement. `top` for tight list rows, `left`
 *                           for a vertical rail — anywhere `bottom` would land
 *                           on top of the thing you're pointing at.
 */
const Tip = memo(function Tip({ label, hint, side = 'bottom', align = 'center', children }) {
    if (!label) return children;

    return (
        <Tooltip>
            {/* `asChild` routes these props through Radix's Slot onto the real
                <button>, so `aria-label` lands on the element that needs it —
                and a child that already declares one still wins the merge. */}
            <TooltipTrigger asChild aria-label={label}>
                {children}
            </TooltipTrigger>
            <TooltipContent side={side} align={align}>
                <span>{label}</span>
                {hint ? <kbd className={KBD_CLASSES}>{hint}</kbd> : null}
            </TooltipContent>
        </Tooltip>
    );
});

export default Tip;
